import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Waybill } from '../../models/Waybill';
import { formatDate } from '../../utils/common';
import { useDataProvider } from 'react-admin';

interface WaybillSettlementDialogProps {
  open: boolean;
  onClose: () => void;
  waybill: Waybill | null;
  onConfirm?: (payload: {
    waybillId: string;
    tonnage: number;
    unitPrice: number;
    settlementPrice: number;
  }) => void;
}

const parseAlipayBalance = (raw: unknown): number | null => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof raw === 'object') {
    const record = raw as Record<string, unknown>;
    const value = record.balance ?? record.available_balance ?? record.data;
    return parseAlipayBalance(value);
  }
  return null;
};

export const WaybillSettlementDialog: React.FC<WaybillSettlementDialogProps> = ({ open, onClose, waybill, onConfirm }) => {
  const dataProvider = useDataProvider();
  const [tonnage, setTonnage] = useState<number>(0);
  const [unitPrice, setUnitPrice] = useState<number>(0);
  const [settlementPrice, setSettlementPrice] = useState<number>(0);
  const [serverAmount, setServerAmount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [canSettle, setCanSettle] = useState<boolean>(false);
  const [serverSinglePrice, setServerSinglePrice] = useState<number | null>(null);
  const [fixedField, setFixedField] = useState<'tonnage' | 'unit'>('tonnage');
  const [alipayBalance, setAlipayBalance] = useState<number | null>(null);
  const [alipayBalanceLoading, setAlipayBalanceLoading] = useState<boolean>(false);
  const [alipayBalanceError, setAlipayBalanceError] = useState<string>('');
  const [confirmPayOpen, setConfirmPayOpen] = useState<boolean>(false);

  const backendPrice = useMemo(() => serverAmount, [serverAmount]);

  const expectLoad = useMemo(() => {
    if (!waybill) return 0;
    const weightsExpect = Number(waybill.weights?.expect_load || 0);
    const cargoExpect = (waybill as any)?.cargo?.expect_load ? Number((waybill as any).cargo.expect_load) : 0;
    return weightsExpect || cargoExpect || 0;
  }, [waybill]);

  useEffect(() => {
    if (!open || !waybill) return;
    const fetchSettlement = async () => {
      try {
        setLoading(true);
        setError('');
        const resp: any = await (dataProvider as any).querySettlementInfo(waybill.id);
        const data = resp?.data || resp;
        const amountNum = Number(data?.amount || 0);
        const weightNum = Number(data?.weight || 0);
        const unitNum = Number(data?.unit_price || 0);
        const singleNum = data?.single_price != null ? Number(data.single_price) : null;
        setServerAmount(amountNum);
        setTonnage(weightNum);
        setUnitPrice(unitNum);
        setSettlementPrice(amountNum);
        setServerSinglePrice(singleNum);
        setCanSettle(true);
      } catch (e: any) {
        setError('这个订单暂不支持该结算操作');
        setCanSettle(false);
      } finally {
        setLoading(false);
      }
    };
    fetchSettlement();
  }, [open, waybill, dataProvider]);

  useEffect(() => {
    if (!open) return;
    const fetchBalance = async () => {
      try {
        setAlipayBalanceLoading(true);
        setAlipayBalanceError('');
        const resp: any = await (dataProvider as any).getAlipayBalance();
        const data = resp?.data ?? resp;
        const balance = parseAlipayBalance(data);
        if (balance === null) {
          setAlipayBalance(null);
          setAlipayBalanceError('无法解析支付宝余额接口返回值');
          return;
        }
        setAlipayBalance(balance);
      } catch (e: any) {
        setAlipayBalance(null);
        setAlipayBalanceError('查询支付宝余额失败：接口不可用或权限不足');
      } finally {
        setAlipayBalanceLoading(false);
      }
    };
    fetchBalance();
  }, [open, dataProvider]);

  const remainingBalance = useMemo(() => {
    if (alipayBalance === null) return null;
    const remain = Number((alipayBalance - settlementPrice).toFixed(2));
    return remain;
  }, [alipayBalance, settlementPrice]);

  const canPayByBalance = useMemo(() => {
    if (alipayBalanceLoading) return false;
    if (alipayBalanceError) return false;
    if (alipayBalance === null) return false;
    if (remainingBalance === null) return false;
    return remainingBalance >= 0;
  }, [alipayBalance, alipayBalanceError, alipayBalanceLoading, remainingBalance]);

  const handleTonnageChange = (v: string) => {
    const n = Number(v);
    setTonnage(isNaN(n) ? 0 : n);
    if (serverSinglePrice != null) {
      return;
    }
    const next = Number(((isNaN(n) ? 0 : n) * unitPrice).toFixed(2));
    setSettlementPrice(next);
  };

  const handleUnitPriceChange = (v: string) => {
    if (serverSinglePrice != null) return;
    const n = Number(v);
    setUnitPrice(isNaN(n) ? 0 : n);
    const next = Number((tonnage * (isNaN(n) ? 0 : n)).toFixed(2));
    setSettlementPrice(next);
  };

  const handleSettlementPriceChange = (v: string) => {
    if (serverSinglePrice != null) return;
    const n = Number(v);
    const price = isNaN(n) ? 0 : n;
    setSettlementPrice(price);
    if (fixedField === 'tonnage') {
      const t = tonnage || 0;
      const nextUnit = t > 0 ? Number((price / t).toFixed(2)) : 0;
      setUnitPrice(nextUnit);
    } else {
      const u = unitPrice || 0;
      const nextTonnage = u > 0 ? Number((price / u).toFixed(2)) : 0;
      setTonnage(nextTonnage);
    }
  };

  const diffRatio = useMemo(() => {
    if (!backendPrice) return 0;
    return Math.abs(settlementPrice - backendPrice) / backendPrice;
  }, [backendPrice, settlementPrice]);

  const overweight = useMemo(() => {
    if (!expectLoad) return false;
    return tonnage > expectLoad;
  }, [tonnage, expectLoad]);

  const handleSubmitSettlement = async () => {
    if (!waybill) return;
    if (!canSettle) return;
    if (!canPayByBalance) {
      setError('支付宝余额不足或无法校验余额，暂不允许继续支付运费。');
      return;
    }
    if (serverSinglePrice != null) {
      const diff = Math.abs(settlementPrice - serverSinglePrice);
      if (diff > 0.01) {
        setError('按车结算金额与系统一口价差异超过 0.01 元，请检查后再提交。');
        return;
      }
    }
    if (diffRatio >= 0.1) {
      const ok = window.confirm('当前结算价与系统建议价差异较大，是否继续提交？');
      if (!ok) return;
    }
    try {
      setSubmitting(true);
      setError('');
      const single = serverSinglePrice != null ? settlementPrice : '';
      const resp: any = await (dataProvider as any).commitSettlementData(waybill.id, {
        amount: settlementPrice,
        weight: tonnage,
        unit_price: unitPrice,
        single_price: single,
      });
      const result = resp?.data || resp;
      void result;
      onConfirm?.({
        waybillId: waybill.id,
        tonnage,
        unitPrice,
        settlementPrice,
      });
      onClose();
    } catch (e: any) {
      setError('提交结算失败：可能是权限不足或接口不可用。');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (!waybill) return;
    if (!canSettle) return;
    if (!canPayByBalance) {
      setError('支付宝余额不足或无法校验余额，暂不允许继续支付运费。');
      return;
    }
    if (serverSinglePrice != null) {
      const diff = Math.abs(settlementPrice - serverSinglePrice);
      if (diff > 0.01) {
        setError('按车结算金额与系统一口价差异超过 0.01 元，请检查后再提交。');
        return;
      }
    }
    if (diffRatio >= 0.1) {
      const ok = window.confirm('当前结算价与系统建议价差异较大，是否继续提交？');
      if (!ok) return;
    }
    setConfirmPayOpen(true);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>结款确认</DialogTitle>
      <DialogContent dividers>
        {waybill && (
          <Box sx={{ mb: 2 }}>
            {loading && (
              <Alert severity="info" sx={{ mb: 2 }}>
                正在查询结算信息...
              </Alert>
            )}
            {alipayBalanceLoading && (
              <Alert severity="info" sx={{ mb: 2 }}>
                正在查询支付宝余额...
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {alipayBalanceError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {alipayBalanceError}
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={6}>
                <Typography variant="body2">单号：{waybill.id}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">司机：{waybill.driver?.name}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">车牌：{waybill.truck?.plate}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">货名：{waybill.cargo?.name}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2">时间：{formatDate(waybill.cargo?.updatedAt)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2">
                  结算类型：{serverSinglePrice != null ? '按车结算' : '按吨结算'}
                </Typography>
              </Grid>
            </Grid>
            {canSettle && (
              <TextField
                label="系统建议结算价"
                value={backendPrice}
                InputProps={{ readOnly: true }}
                fullWidth
                margin="dense"
                size="small"
              />
            )}
            {canSettle && alipayBalance !== null && remainingBalance !== null && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  支付宝余额：{alipayBalance.toFixed(2)} 元
                </Typography>
                <Typography variant="body2" color={remainingBalance >= 0 ? "text.secondary" : "error.main"}>
                  预计付款后剩余：{remainingBalance.toFixed(2)} 元
                </Typography>
                {remainingBalance < 0 && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    余额不足，无法继续支付运费。
                  </Alert>
                )}
              </Box>
            )}
            {canSettle && diffRatio >= 0.1 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                当前结算价与系统建议价差异较大，请确认。
              </Alert>
            )}
            {canSettle && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={4}>
                {serverSinglePrice == null && (
                  <FormControlLabel
                    control={<Checkbox checked={fixedField === 'tonnage'} onChange={() => setFixedField('tonnage')} />}
                    label="吨位固定"
                  />
                )}
                <TextField
                  label="吨位(吨)"
                  type="number"
                  inputProps={{ step: '0.01' }}
                  value={tonnage}
                  onChange={(e) => handleTonnageChange(e.target.value)}
                  fullWidth
                  size="small"
                  helperText={overweight ? `超过预估吨位(${expectLoad})，请确认` : ''}
                  color={overweight ? 'warning' : 'primary'}
                />
              </Grid>
              <Grid item xs={4}>
                {serverSinglePrice == null && (
                  <FormControlLabel
                    control={<Checkbox checked={fixedField === 'unit'} onChange={() => setFixedField('unit')} />}
                    label="单价固定"
                  />
                )}
                <TextField
                  label={serverSinglePrice != null ? '单价(元/车)' : '单价(元/吨)'}
                  type="number"
                  inputProps={{ step: '0.01' }}
                  value={unitPrice}
                  onChange={(e) => handleUnitPriceChange(e.target.value)}
                  fullWidth
                  size="small"
                  disabled={serverSinglePrice != null}
                />
              </Grid>
              <Grid item xs={4}>
                {serverSinglePrice == null && (
                  <FormControlLabel
                    control={<Checkbox />}
                    label="占位"
                    sx={{ visibility: 'hidden' }}
                  />
                )}
                <TextField
                  label="结算价(元)"
                  type="number"
                  inputProps={{ step: '0.01' }}
                  value={settlementPrice}
                  onChange={(e) => handleSettlementPriceChange(e.target.value)}
                  fullWidth
                  size="small"
                  disabled={serverSinglePrice != null}
                />
              </Grid>
            </Grid>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting || loading}>取消</Button>
        <Button onClick={handleConfirm} variant="contained" color="primary" disabled={!canSettle || submitting || loading || !canPayByBalance}>确认结算</Button>
      </DialogActions>
      <Dialog open={confirmPayOpen} onClose={() => setConfirmPayOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>支付运费确认</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              支付宝余额：{alipayBalance === null ? "-" : `${alipayBalance.toFixed(2)} 元`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              本次付款：{settlementPrice.toFixed(2)} 元
            </Typography>
            <Typography variant="body2" color={remainingBalance !== null && remainingBalance >= 0 ? "text.secondary" : "error.main"}>
              付款后剩余：{remainingBalance === null ? "-" : `${remainingBalance.toFixed(2)} 元`}
            </Typography>
            {!canPayByBalance && (
              <Alert severity="error" sx={{ mt: 1 }}>
                余额不足或无法校验余额，无法继续支付。
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPayOpen(false)} disabled={submitting}>取消</Button>
          <Button onClick={handleSubmitSettlement} variant="contained" disabled={submitting || !canPayByBalance}>确认支付</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default WaybillSettlementDialog;
